
import "../../../../../styles/questionnaire/vehicles.less";
import { FC, KeyboardEvent, useEffect, useState, useContext } from "react";

import { SearchOutlined } from "@ant-design/icons";
import { Button, Collapse, Form, Input, Progress } from "antd";
import { AppContext } from "App";
import { useFetchVehiclesMutation } from "services/vehicles";

import { Question } from "../../constants/questions";

type VehicleQuestionProps = {
    data: Question;
    onAnswer: (_question: string, _answer: string | undefined) => void;
    categoryAnswers: Record<string, any>;
};

const VehicleQuestion: FC<VehicleQuestionProps> = ({ data, onAnswer, categoryAnswers }) => {
    const { isMobile } = useContext(AppContext);
    const [fetchVehiclesMutation] = useFetchVehiclesMutation();
    const { Search } = Input;
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [activeKey] = useState<string>("1");
    const [percent, setPercent] = useState(0);
    const [vehicleData, setVehicleData] = useState<any>(null);
    const [showResults, setShowResults] = useState(true);

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === "Enter") {
            isMobile ? e.currentTarget.blur() : e.preventDefault();
        }
    };

    const handleSearch = async (value: string) => {
        try {
            if (!value || value.trim() === "") {
                return;
            }
            setPercent(0);
            setError(null);
            setLoading(true);
            const response = await fetchVehiclesMutation({
                registrationNumber: inputValue,
            }).unwrap();

            setTimeout(() => {
                onAnswer(data.question, response);
                setVehicleData(response);
                setShowResults(true);
                setLoading(false);
            }, 2000);
        } catch (err: any) {
            setError("Vehicle registration not found.");
            setVehicleData(null);
            setLoading(false);
        }
    };

    const handleChange = (e: any) => {
        setInputValue(e.target.value);
        setError(null);
        if (e.target.value === "") {
            setLoading(false);
            setShowResults(false);
        }
    };

    const handleTextChange = (field: string, value: string) => {
        setVehicleData((prev: any) => ({
            ...prev,
            [field]: value,
        }));
        onAnswer(data.question, {
            ...vehicleData,
            [field]: value,
        });
    };

    useEffect(() => {
        const savedVehicle = categoryAnswers?.find(
            (item: any) => item.id === "CIS_CAR_MAKE_MODEL" || item.id === "SELF_EMPLOYMENT_CAR_MAKE_MODEL"
        )?.answer;
        if (savedVehicle && savedVehicle.registrationNumber) {
            setVehicleData((prev: any) => ({
                ...prev,
                ...savedVehicle,
            }));
            setInputValue(savedVehicle.registrationNumber);
        }
    }, [categoryAnswers]);

    useEffect(() => {
        if (percent < 100) {
            const interval = setInterval(() => {
                setPercent(prev => prev + 1);
            }, 50);
            return () => clearInterval(interval);
        }
    }, [percent]);

    return (
        <div className="vehicles__container">
            {data.question && (
                <div className="vehicles__title" >{data.question}</div>
            )}
            <div className="mb20">
                <div className="mb10">Please enter your vehicle’s registration plate number</div>
                <div>
                    <Search
                        allowClear
                        placeholder="Enter car registration"
                        value={inputValue}
                        onSearch={handleSearch}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        enterButton={
                            <Button type="primary" icon={<SearchOutlined />}>
                                Search
                            </Button>
                        }
                    />
                </div>
            </div>
            {error && <p style={{ color: "red" }}>{error}</p>}
            {loading ? (
                <div className="vehicles__loading">
                    Searching
                    <Progress
                        percent={percent}
                        strokeColor={"#7F43FF"}
                        showInfo={false}
                        size="small"
                        className="mb20"
                    />
                </div>
            )
                : (vehicleData && showResults && (
                    <Collapse activeKey={activeKey}
                        style={{ marginBottom: "100px" }}
                        items={[
                            {
                                key: "1",
                                label: "Vehicle Details",
                                className: "vehicles__panel",
                                children: (
                                    <>
                                        <div className= "vehicles__outer-input">
                                            <Form.Item noStyle>
                                                <div style={{ width: "50%" }}> Registration plate:</div>
                                                <input type="text" value={vehicleData.registrationNumber || ""} className="vehicles__input" readOnly/>
                                            </Form.Item>
                                        </div>
                                        <div className= "vehicles__outer-input">
                                            <Form.Item noStyle>
                                                <div style={{ width: "50%" }}>  Registration Year:</div>
                                                <input type="text" value={vehicleData.monthOfFirstRegistration || ""} className="vehicles__input" readOnly/>
                                            </Form.Item>
                                        </div>
                                        <div className= "vehicles__outer-input">
                                            <Form.Item noStyle>
                                                <div style={{ width: "50%" }}> Make: </div>
                                                <input type="text" value={ vehicleData.make || "" } className="vehicles__input" readOnly/>
                                            </Form.Item>
                                        </div>
                                        <div className= "vehicles__outer-input">
                                            <Form.Item noStyle>
                                                <div style={{ width: "50%" }}> CO<sub>2</sub> Emissions: </div>
                                                <input
                                                    type="text"
                                                    value={vehicleData.co2Emissions || ""}
                                                    onChange={e => handleTextChange("co2Emissions", e.target.value)}
                                                    className="vehicles__input"
                                                />
                                            </Form.Item>
                                        </div>
                                        <div className= "vehicles__outer-input">
                                            <Form.Item noStyle>
                                                <div style={{ width: "50%" }}> Fuel Type: </div>
                                                <input type="text" value={vehicleData.fuelType || ""} className="vehicles__input" readOnly/>
                                            </Form.Item>
                                        </div>
                                        <div className= "vehicles__outer-input">
                                            <Form.Item noStyle>
                                                <div style={{ width: "50%" }}> Colour: </div>
                                                <input type="text" value={vehicleData.colour || ""} className="vehicles__input" readOnly/>
                                            </Form.Item>
                                        </div>
                                    </>
                                ),
                            },
                        ]}
                    />
                ))
            }
        </div>
    );
};
export default VehicleQuestion;
